/**
 * LitSquad avatar transform data + helpers.
 *
 * The generated data files (`character-indices.generated.ts`,
 * `accessory-transforms.generated.ts`) are produced from the upstream
 * LitLegion Unity project — see `scripts/extract-litsquad-avatar-transforms.ts`.
 * The hand-written `coordinates.ts` module turns Unity world coordinates
 * into canvas pixels.
 *
 * Public surface:
 *   - {@link lookupAccessoryTransform} — given an accessory identifier and
 *     a species, returns the per-character variant the avatar editor draws.
 *   - {@link getLitSquadCharacterIndex} — case-insensitive species → 0..16
 *     index lookup. Wraps the generated map so callers can pass either
 *     PascalCase (`'Bear'`) or lowercase (`'bear'`) — metadata stores
 *     PascalCase while `getLitSquadSpecies` returns lowercase.
 */

import {
  LITSQUAD_CHARACTER_INDEX,
  LITSQUAD_SPECIES_BY_INDEX,
  type LitSquadSpeciesName,
  getLitSquadCharacterIndex as getLitSquadCharacterIndexCaseSensitive,
} from './character-indices.generated';
import {
  DEFAULT_HANDS_TYPE,
  LITSQUAD_ACCESSORY_TRANSFORMS,
  type AccessoryTransform,
  type AccessoryTransformEntry,
  type AccessoryTransformsByCharacter,
} from './accessory-transforms.generated';

export {
  LITSQUAD_CHARACTER_INDEX,
  LITSQUAD_SPECIES_BY_INDEX,
  LITSQUAD_ACCESSORY_TRANSFORMS,
  DEFAULT_HANDS_TYPE,
};
export type {
  AccessoryTransform,
  AccessoryTransformEntry,
  AccessoryTransformsByCharacter,
  LitSquadSpeciesName,
};
/**
 * Case-insensitive species → character index lookup. `getLitSquadSpecies`
 * returns lowercase (`'bear'`) while metadata and the type-level
 * `LitSquadSpecies` are PascalCase (`'Bear'`); both should resolve.
 */
export function getLitSquadCharacterIndex(species: string | null | undefined): number | null {
  if (!species) return null;
  // Try exact match first (PascalCase) — fastest path.
  const exact = getLitSquadCharacterIndexCaseSensitive(species);
  if (exact !== null) return exact;
  // Fall back to case-insensitive match against the canonical keys.
  const lower = species.toLowerCase();
  for (const key of Object.keys(LITSQUAD_CHARACTER_INDEX) as LitSquadSpeciesName[]) {
    if (key.toLowerCase() === lower) {
      return LITSQUAD_CHARACTER_INDEX[key];
    }
  }
  return null;
}

/**
 * The transforms keyed by trimmed identifier. A few upstream identifiers carry
 * a trailing space (`'holdingRight_morphing '`), which the catalog and the
 * Unity asset keep verbatim, while {@link getAccessoryIdentifier} trims what it
 * reads — so an exact lookup alone would miss them and draw the accessory
 * stretched instead of placed.
 */
const TRANSFORMS_BY_TRIMMED_ID = new Map<string, AccessoryTransformEntry>(
  Object.entries(LITSQUAD_ACCESSORY_TRANSFORMS).map(([id, entry]) => [id.trim(), entry]),
);

/**
 * Returns the transform variant the avatar editor should render for
 * a given accessory + species. Picks the variant whose `handsType`
 * matches `DEFAULT_HANDS_TYPE` (the standard standing pose), falling
 * back to the first variant in the bucket if there is no match.
 *
 * Returns `null` when the accessory is unknown, the species is
 * unknown, or the upstream Unity asset has no entry for that
 * character (rare — most accessories cover all 17 species).
 */
export function lookupAccessoryTransform(
  identifier: string | null | undefined,
  species: string | null | undefined,
): AccessoryTransform | null {
  if (!identifier) return null;
  const entry =
    LITSQUAD_ACCESSORY_TRANSFORMS[identifier] ?? TRANSFORMS_BY_TRIMMED_ID.get(identifier.trim());
  if (!entry) return null;

  const characterIndex = getLitSquadCharacterIndex(species);
  if (characterIndex === null) return null;

  const variants = entry.perCharacter[characterIndex];
  if (!variants || variants.length === 0) return null;

  const defaultVariant = variants.find((v) => v.handsType === DEFAULT_HANDS_TYPE);
  return defaultVariant ?? variants[0];
}

/**
 * Pulls the accessory identifier out of an inventory entry's metadata —
 * the legacy `Identifier` key, which imported and crafted accessories
 * both carry.
 *
 * Returns `null` when the entry has no identifier — the caller should
 * skip transform application and fall back to a stretch-to-fill render.
 */
export function getAccessoryIdentifier(
  metadata: Record<string, unknown> | null | undefined,
): string | null {
  const value = metadata?.Identifier;
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

/**
 * The accessory group (`HeadItems`, `Shirts`, …) an accessory occupies — the
 * legacy `Group` key. An avatar wears at most one accessory per group; the
 * server refuses a recipe with two, and the editor swaps one for the other.
 * Null when the entry declares no group.
 */
export function getAccessoryGroup(
  metadata: Record<string, unknown> | null | undefined,
): string | null {
  const value = metadata?.Group;
  return typeof value === 'string' && value.length > 0 ? value : null;
}
